import { memo, useCallback, useState, type ReactNode } from 'react';
import { Ban, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { KanbanCardSummary } from '../../types/kanban';
import { KanbanCardMini } from './KanbanCardMini';
import { KanbanCardTypeBadge } from './KanbanCardTypeBadge';

export interface KanbanStackedCardsProps {
  root: KanbanCardSummary;
  nested: KanbanCardSummary[];
  onClick: (cardId: string) => void;
  wipWarningHours?: number;
  swimlaneBadge?: { title: string; color: string | null } | null;
  draggable?: boolean;
  /** When true, nested subtasks can be dragged alone to reparent (root drag is disabled while a nested drag is active from this stack). */
  nestedDraggable?: boolean;
  /** While a nested card from this stack is being dragged solo, the root card is not draggable. */
  rootDragFrozen?: boolean;
  onDragStart?: (e: React.DragEvent, cardId: string) => void;
  onDragEnd?: (e: React.DragEvent, cardId: string) => void;
  isDraggingStack?: boolean;
  /** Drop line between root and nested, or between nested cards (flat insert within stack). */
  intraStackDropSlot?: 'afterRoot' | { afterNestedIndex: number } | null;
  intraStackDropIndicator?: ReactNode;
}

export const KanbanStackedCards = memo(
  ({
    root,
    nested,
    onClick,
    wipWarningHours,
    swimlaneBadge,
    draggable = true,
    nestedDraggable = false,
    rootDragFrozen = false,
    onDragStart,
    onDragEnd,
    isDraggingStack,
    intraStackDropSlot = null,
    intraStackDropIndicator = null,
  }: KanbanStackedCardsProps) => {
    const [expandedNestedIds, setExpandedNestedIds] = useState<Set<string>>(() => new Set());

    const toggleNestedExpanded = useCallback((id: string) => {
      setExpandedNestedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);

    return (
      <div
        data-kanban-stack-root={root.id}
        className={cn(
          'relative rounded-xl',
          isDraggingStack && 'opacity-[0.42] scale-[0.985]',
        )}
      >
        {/* Connector spine */}
        <div
          className="pointer-events-none absolute left-[18px] top-10 bottom-[calc(0.75rem+4px)] w-px bg-accent-400/30"
          aria-hidden
        />

        <div className="relative z-[1] flex flex-col gap-0">
          <div className="relative rounded-xl">
            <KanbanCardMini
              card={root}
              onClick={onClick}
              wipWarningHours={wipWarningHours}
              swimlaneBadge={swimlaneBadge}
              draggable={draggable && !rootDragFrozen}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              className="rounded-b-none border-b-0"
            />
          </div>
          {intraStackDropSlot === 'afterRoot' ? intraStackDropIndicator : null}

          {nested.map((sub, i) => {
            const isLast = i === nested.length - 1;
            const isExpanded = expandedNestedIds.has(sub.id);
            const isBlocked = sub.is_blocked === 1;

            return (
              <div key={sub.id} className="relative -mt-[10px] pt-[6px]">
                {/* Curved branch */}
                <svg
                  className="pointer-events-none absolute left-[10px] top-0 h-[22px] w-[18px] text-accent-400/45"
                  viewBox="0 0 18 22"
                  fill="none"
                  aria-hidden
                >
                  <path
                    d="M 8 0 L 8 10 Q 8 14 12 14 L 18 14"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                {isExpanded ? (
                  <KanbanCardMini
                    card={sub}
                    onClick={onClick}
                    wipWarningHours={wipWarningHours}
                    swimlaneBadge={swimlaneBadge}
                    draggable={nestedDraggable}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    className={cn(
                      'rounded-t-none border-t border-white/[0.06] pl-5',
                      !isLast && 'rounded-b-none border-b-0',
                    )}
                  />
                ) : (
                  <div
                    data-kanban-card={sub.id}
                    draggable={nestedDraggable}
                    onDragStart={(e) => {
                      if (!nestedDraggable) return;
                      e.stopPropagation();
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', sub.id);
                      e.dataTransfer.setData('application/x-kanban-card-id', sub.id);
                      onDragStart?.(e, sub.id);
                    }}
                    onDragEnd={(e) => {
                      if (!nestedDraggable) return;
                      e.stopPropagation();
                      onDragEnd?.(e, sub.id);
                    }}
                    className={cn(
                      'glass-card cursor-pointer group overflow-hidden transition-colors duration-150',
                      'border-t border-white/[0.06] pl-5 rounded-t-none',
                      'hover:border-accent-400/35 hover:bg-glass-100/5',
                      isBlocked &&
                        'border-l-2 border-red-500 ring-1 ring-inset ring-red-500/25 bg-red-500/5 hover:bg-red-500/[0.08]',
                      !isLast && 'rounded-b-none border-b-0',
                    )}
                  >
                    <div className="relative flex gap-1.5 p-2.5 pr-2 items-start">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleNestedExpanded(sub.id);
                        }}
                        className={cn(
                          'btn-icon btn-icon-sm mt-0.5 shrink-0',

                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/50',
                        )}
                        aria-expanded={false}
                        aria-label="Expand subtask"
                      >
                        <ChevronDown className="w-4 h-4" aria-hidden />
                      </button>
                      <div
                        role="button"
                        tabIndex={0}
                        className="min-w-0 flex-1 text-left"
                        onClick={() => onClick(sub.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onClick(sub.id);
                          }
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <KanbanCardTypeBadge
                            type={sub.card_type}
                            showLabel={false}
                            className="!px-1.5 !py-0 !text-[10px] shrink-0"
                          />
                          <h4 className="flex-1 min-w-0 text-sm font-medium text-white line-clamp-2 leading-snug">
                            {sub.title}
                          </h4>
                        </div>
                      </div>
                    </div>
                    {isBlocked ? (
                      <div className="px-2.5 pb-2.5 pl-11">
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-red-500/25 bg-red-500/10 text-red-300 text-[11px]">
                          <Ban className="w-3.5 h-3.5 shrink-0" aria-hidden />
                          <span className="min-w-0 truncate">{sub.blocked_reason ?? 'Blocked'}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
                {isExpanded ? (
                  <div className="flex justify-end px-2 pb-1 -mt-0.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleNestedExpanded(sub.id);
                      }}
                      className="btn-ghost btn-xs text-[11px]"
                      aria-label="Collapse subtask"
                    >
                      <ChevronUp className="w-3.5 h-3.5" aria-hidden />
                      Collapse
                    </button>
                  </div>
                ) : null}
                {intraStackDropSlot &&
                typeof intraStackDropSlot === 'object' &&
                intraStackDropSlot.afterNestedIndex === i
                  ? intraStackDropIndicator
                  : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

KanbanStackedCards.displayName = 'KanbanStackedCards';
